<script lang="ts" setup>
	export type FrameClass = {
		base: string
		overflow: string
		img: Partial<{
			base: string
			width: string
			height: string
		}>
		imgWrapper: Partial<{
			base: string
			height: string
			width: string
			position: string
			rounded: string
		}>
	}

	const configClass: FrameClass = {
		base: '',
		overflow: '',
		img: {
			base: 'object-cover',
			width: '',
			height: 'md:max-h-[456px] max-h-[400px]'
		},
		imgWrapper: {
			height: 'md:min-h-[541px]',
			width: 'w-full max-sm:ml-auto max-sm:max-w-[290px] sm:min-w-[456px] ',
			position: 'relative',
			rounded: 'rounded '
		}
	}

	const { img, imgWrapper, base, overflow } = configClass
	defineProps<{
		src: string
		alt?: string
		ui?: Partial<FrameClass>
	}>()

	const defaultText = {
		icon: 'fire-damage',
		label: 'Fire damage covered'
	}
	const careList = [
		{
			icon: 'full-coverage',
			label: 'Full Coverage'
		},
		{
			icon: 'excess-buy-bag',
			label: 'Excess buyback covered'
		},
		{
			icon: 'damaged-car',
			label: 'Accidental damage covered'
		}
	]
</script>

<template>
	<div :class="[ui?.base ?? base, ui?.overflow ?? overflow, 'relative max-sm:w-full']">
		<ul
			class="absolute max-w-[202px] md:max-w-[300px] bottom-8 md:bottom-28 my-auto pointer-events-none pt-1 wrap custom-transition w-full lg:-left-28 left-0 inline-flex h-auto flex-col z-10"
		>
			<li
				:class="$style['list-effect']"
				class="flex list- relative z-20 transition-all overflow-hidden h-9 md:h-[56px] rounded-[6px] justify-between gap-x-5 w-full px-[9.33px] md:px-5 bg-white"
			>
				<div class="custom-transition flex items-center flex-none gap-x-2">
					<tools-icon
						:icon="defaultText.icon"
						dir="random"
						:height="18"
						:width="18"
						svg-class="sm:w-[28px] sm:h-[28px] w-[16px] h-[16px]"
					/>
					<span class="xs:text-xs text-[10px] font-medium w-full text-health-900">{{
						defaultText.label
					}}</span>
				</div>

				<!-- <div class="flex custom-transition items-center gap-x-2">
					<tools-icon
						:icon="careList[0].icon"
						dir="random"
						:height="18"
						:width="18"
						svg-class="sm:w-[28px] sm:h-[28px] w-[16px] h-[16px]"
					/>
					<span class="xs:text-xs text-[10px] font-medium text-health-900">{{ careList[0].label }}</span>
				</div> -->
				<div class="flex custom-transition opacity-0 items-center gap-x-2">
					<tools-icon
						:icon="careList[0].icon"
						dir="random"
						:height="18"
						:width="18"
						svg-class="sm:w-[28px] sm:h-[28px] w-[16px] h-[16px]"
					/>
					<span class="xs:text-xs text-[8px] font-medium text-health-900">{{ careList[0].label }}</span>
				</div>

				<div class="flex items-center gap-x-3 flex-none">
					<div
						class="flex-none max-w-[40px] overflow-hidden min-h-[30px] min-w-[20px] px-2 relative inline-flex justify-end w-full items-end flex-col"
					>
						<tools-icon dir="random" icon="umbrella-3" />
					</div>
				</div>
			</li>
			<li
				v-for="(item, index) in careList"
				:key="index"
				:class="$style['list-effect']"
				class="flex absolute z-10 overflow-hidden h-9 md:h-[56px] rounded-[6px] justify-between gap-x-5 w-full px-[9.33px] md:px-5 bg-white"
			>
				<div class="flex items-center gap-x-2">
					<tools-icon
						:icon="item.icon"
						dir="random"
						:height="18"
						:width="18"
						svg-class="sm:w-[28px] sm:h-[28px] w-[16px] h-[16px]"
					/>
					<span class="xs:text-xs text-[8px] font-medium text-health-900">{{ item.label }}</span>
				</div>

				<div class="flex items-center gap-x-3 flex-none">
					<div
						class="flex-none max-w-[40px] overflow-hidden min-h-[30px] min-w-[20px] px-2 relative inline-flex justify-end w-full items-end flex-col"
					>
						<tools-icon dir="random" icon="umbrella-3" />
					</div>
				</div>
			</li>
		</ul>

		<div
			:class="[
				ui?.imgWrapper?.base ?? imgWrapper.base,
				ui?.imgWrapper?.height ?? imgWrapper.height,
				ui?.imgWrapper?.position ?? imgWrapper.position,
				ui?.imgWrapper?.rounded ?? imgWrapper.rounded,
				ui?.imgWrapper?.width ?? imgWrapper.width
			]"
		>
			<nuxt-img
				provider="cloudinary"
				loading="lazy"
				:src="src"
				:alt="alt || ''"
				:class="[ui?.img?.base ?? img.base, ui?.img?.height ?? img.height, ui?.img?.width ?? img.width]"
				preload
			/>
		</div>
	</div>
</template>

<style module>
	.list-effect {
		--position: 0;
		animation: stretch 2s cubic-bezier(0.43, 0.2, 0, 0.8) infinite alternate;
		top: 0;
		animation-composition: replace, add;
	}

	.list-effect:nth-child(1) {
		--position: -50px;
		z-index: 40;
	}

	.list-effect:last-child {
		top: 50px;
		--position: 50px;
	}

	@media screen and (min-width: 768px) {
		.list-effect:nth-child(1) {
			--position: -80px;
		}
		.list-effect:last-child {
			top: 80px;
			--position: 80px;
		}
	}

	.list-effect .text-effect:last-child {
		animation: text-switch 1ms ease-out infinite alternate;
	}
	.list-effect .text-effect {
		position: absolute;
		top: 0;
		bottom: 0;
		margin-block: auto;
		visibility: hidden;
		opacity: 0;
		animation-composition: replace, add;
		animation: text-switch 2s cubic-bezier(0.47, 0, 0.745, 0.715) infinite alternate-reverse;
	}

	@keyframes text-switch {
		0% {
			visibility: hidden;
			opacity: 0;
		}
		100% {
			visibility: visible;
			opacity: 1;
		}
	}

	@keyframes stretch {
		from {
			top: 0;
		}
		to {
			top: var(--position);
		}
	}
</style>
